import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Target, Eye, Sparkles, Rocket, Heart, Award, ChevronRight, ArrowRight, Linkedin } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Animated counter component
const AnimatedCounter = ({ value, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  
  useEffect(() => {
    if (isInView) {
      const num = parseInt(value.replace(/[^0-9]/g, '')) || 0;
      const duration = 1500;
      const steps = 40;
      const increment = num / steps;
      let current = 0;
      
      const timer = setInterval(() => {
        current += increment;
        if (current >= num) {
          setCount(num);
          clearInterval(timer);
        } else {
          setCount(Math.floor(current));
        }
      }, duration / steps);
      
      return () => clearInterval(timer);
    }
  }, [isInView, value]);
  
  return <span ref={ref}>{count}{suffix}</span>;
};

// Value Row Component - Compact horizontal list item (no icons)
const ValueRow = ({ value, index, isLast, hasBgImage }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`group flex items-center justify-between py-4 px-6 cursor-pointer transition-all duration-200 ${
        isHovered ? 'bg-[#2a2a2a] text-white' : hasBgImage ? 'bg-transparent text-[#320547] hover:bg-white/50' : 'bg-white text-[#320547] hover:bg-[#f8f9fa]'
      } ${!isLast ? 'border-b border-[#e5e7eb]' : ''}`}
      data-testid={`value-row-${index}`}
    >
      <span className="font-semibold text-lg">{value.title}</span>
      <motion.p 
        initial={false}
        animate={{ opacity: isHovered ? 1 : 0.6, x: isHovered ? 0 : 10 }}
        className={`text-sm max-w-md text-right hidden md:block ${isHovered ? 'text-white/90' : 'text-[#6b7280]'}`}
      >
        {value.description}
      </motion.p>
      <ArrowRight size={18} className={`md:hidden transition-transform ${isHovered ? 'translate-x-1' : ''}`} />
    </motion.div>
  );
};

// Team Member Card - Expands horizontally only on hover to show bio
const TeamMember = ({ member, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`cursor-pointer transition-all duration-300 ${
        isHovered ? 'col-span-2 z-20' : 'col-span-1 z-10'
      }`}
      data-testid={`team-member-${index}`}
    >
      <div className={`rounded-lg overflow-hidden h-[280px] ${isHovered ? 'flex bg-[#2a2a2a]' : ''}`}>
        {/* Photo - Fixed height, no zoom */}
        <div className={`h-full flex-shrink-0 ${isHovered ? 'w-1/2' : 'w-full'}`}>
          <img 
            src={member.photo} 
            alt={member.name}
            className="w-full h-full object-cover object-top"
          />
        </div>
        
        {/* Expanded Info Panel */}
        {isHovered && (
          <div className="w-1/2 flex flex-col justify-center p-5 overflow-hidden">
            <p className="text-[#95228A] text-xs font-semibold uppercase tracking-wider mb-1">
              {member.position}
            </p>
            <h4 className="text-white font-bold text-lg mb-2">{member.name}</h4>
            <p className="text-white/80 text-sm leading-relaxed mb-3 line-clamp-5">
              {member.bio || 'Passionate about creating exceptional digital experiences.'}
            </p>
            {member.linkedin && (
              <a 
                href={member.linkedin} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm w-fit"
                onClick={(e) => e.stopPropagation()}
              >
                <Linkedin size={16} />
                LinkedIn
              </a>
            )}
          </div>
        )}
      </div>
      
      {/* Name below card (only when not hovered) */}
      {!isHovered && (
        <h4 className="font-semibold text-[#320547] text-base mt-3">
          {member.name}
        </h4>
      )}
    </div>
  );
};

const About = () => {
  const [settings, setSettings] = useState(null);
  const [values, setValues] = useState([]);
  const [team, setTeam] = useState([]);
  const [culture, setCulture] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, valuesRes, teamRes, cultureRes, statsRes] = await Promise.all([
          fetch(`${API_URL}/api/cms/about-settings`),
          fetch(`${API_URL}/api/cms/core-values`),
          fetch(`${API_URL}/api/cms/team-members?is_leadership=true`),
          fetch(`${API_URL}/api/cms/culture-images`),
          fetch(`${API_URL}/api/cms/company-stats`)
        ]);
        
        const [settingsData, valuesData, teamData, cultureData, statsData] = await Promise.all([
          settingsRes.ok ? settingsRes.json() : null,
          valuesRes.ok ? valuesRes.json() : [],
          teamRes.ok ? teamRes.json() : [],
          cultureRes.ok ? cultureRes.json() : [],
          statsRes.ok ? statsRes.json() : []
        ]);
        
        setSettings(settingsData);
        setValues(valuesData);
        setTeam(teamData);
        setCulture(cultureData);
        setStats(statsData);
      } catch (error) {
        console.error('Error fetching about page data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Default data
  const defaultValues = [
    { id: '1', title: 'Innovation First', description: 'Pushing boundaries with cutting-edge solutions', icon: 'Sparkles' },
    { id: '2', title: 'Client Success', description: 'Your success drives everything we do', icon: 'Target' },
    { id: '3', title: 'Technical Excellence', description: 'Highest standards in engineering practices', icon: 'Award' },
    { id: '4', title: 'Transparency', description: 'Open communication, honest feedback', icon: 'Eye' },
    { id: '5', title: 'Agile Mindset', description: 'Adapt quickly, deliver incrementally', icon: 'Rocket' },
    { id: '6', title: 'Passion Driven', description: 'We love creating amazing products', icon: 'Heart' }
  ];

  const defaultStats = [
    { id: '1', value: '50+', label: 'Projects', icon: 'Briefcase' },
    { id: '2', value: '20+', label: 'Team Members', icon: 'Users' },
    { id: '3', value: '10+', label: 'Technologies', icon: 'Code' },
    { id: '4', value: '100%', label: 'Satisfaction', icon: 'Heart' }
  ];

  // No default team - only show if data exists in CMS
  const defaultTeam = [];

  const defaultCulture = [
    { id: '1', image_url: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=400', caption: 'Collaboration' },
    { id: '2', image_url: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=400', caption: 'Innovation' },
    { id: '3', image_url: 'https://images.unsplash.com/photo-1552581234-26160f608093?w=400', caption: 'Growth' }
  ];

  const displayValues = values.length > 0 ? values : defaultValues;
  const displayStats = stats.length > 0 ? stats : defaultStats;
  const displayTeam = team.length > 0 ? team : defaultTeam;
  const displayCulture = culture.length > 0 ? culture : defaultCulture;

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#320547] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      <SEO 
        title="About Us - Navaris Digital"
        description="Learn about Navaris Digital - our values, team, and culture."
        canonicalUrl="/about"
      />
      
      {/* Hero Section - Full Image Background */}
      <section className="relative min-h-[80vh] flex items-center pt-20 overflow-hidden">
        {/* Background Image - Full width */}
        <div className="absolute inset-0 z-0">
          <img 
            src={settings?.hero_image || 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1920&q=80'}
            alt="Office background"
            className="w-full h-full object-cover"
          />
          {/* Subtle overlay for text readability */}
          <div className="absolute inset-0 bg-black/50" />
        </div>
        
        <div className="w-full relative z-10">
          <div className="grid lg:grid-cols-2">
            {/* Left - Large Typography */}
            <div className="px-6 md:px-12 py-16 lg:py-24 flex flex-col justify-center">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-xs font-bold uppercase tracking-[0.2em] text-[#95228A] mb-6"
              >
                About Us
              </motion.p>
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-5xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight leading-[0.9] mb-8"
                data-testid="hero-title"
              >
                {settings?.hero_title || 'We are Navaris Digital'}
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-lg md:text-xl text-white/90 leading-relaxed max-w-lg"
              >
                {settings?.hero_subtitle || 'A passionate team building digital products that make a difference.'}
              </motion.p>
            </div>
            
            {/* Right - Stats Grid */}
            <div className="grid grid-cols-2">
              {displayStats.map((stat, index) => (
                <motion.div
                  key={stat.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className={`p-8 md:p-12 flex flex-col justify-center ${
                    index < 2 ? 'border-b border-white/20' : ''
                  } ${index % 2 === 0 ? 'border-r border-white/20' : ''}`}
                >
                  <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                    <AnimatedCounter value={stat.value} suffix={stat.value.includes('+') ? '+' : stat.value.includes('%') ? '%' : ''} />
                  </div>
                  <p className="text-sm text-white/70 uppercase tracking-wider">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision - Compact Split Pane */}
      <section className="border-b border-[#e5e7eb]">
        <div className="grid md:grid-cols-2">
          {/* Mission */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-8 md:p-12 bg-white border-r border-[#e5e7eb]"
            data-testid="mission-card"
          >
            <div className="flex items-center gap-3 mb-4">
              <Target size={24} className="text-[#320547]" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#6b7280]">Mission</span>
            </div>
            <p className="text-lg md:text-xl text-[#320547] leading-relaxed">
              {settings?.mission || 'Empower businesses with innovative digital solutions that drive growth and lasting success.'}
            </p>
          </motion.div>
          
          {/* Vision */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="p-8 md:p-12 bg-[#f8f9fa]"
            data-testid="vision-card"
          >
            <div className="flex items-center gap-3 mb-4">
              <Eye size={24} className="text-[#95228A]" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#6b7280]">Vision</span>
            </div>
            <p className="text-lg md:text-xl text-[#320547] leading-relaxed">
              {settings?.vision || 'Be the go-to partner for digital transformation, known for excellence and meaningful impact.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Core Values - Compact List with Background Image */}
      <section className="border-b border-[#e5e7eb]">
        <div className="grid lg:grid-cols-3">
          {/* Label Column - With Background Image */}
          <div className="relative p-8 md:p-12 border-b lg:border-b-0 lg:border-r border-[#e5e7eb] flex flex-col justify-center overflow-hidden">
            {/* Background Image - Only on this column */}
            {settings?.core_values_bg_image && (
              <div className="absolute inset-0 z-0">
                <img 
                  src={settings.core_values_bg_image}
                  alt="Core Values Background"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-[#2a2a2a]/50" />
              </div>
            )}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative z-10"
            >
              <p className={`text-xs font-bold uppercase tracking-[0.2em] mb-3 ${settings?.core_values_bg_image ? 'text-white' : 'text-[#95228A]'}`}>What Drives Us</p>
              <h2 className={`text-3xl md:text-4xl font-bold tracking-tight ${settings?.core_values_bg_image ? 'text-white' : 'text-[#320547]'}`}>
                Core Values
              </h2>
            </motion.div>
          </div>
          
          {/* Values List - White background */}
          <div className="lg:col-span-2 bg-white">
            {displayValues.map((value, index) => (
              <ValueRow 
                key={value.id} 
                value={value} 
                index={index} 
                isLast={index === displayValues.length - 1}
                hasBgImage={false}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Team - Dynamic from CMS - Only show if team members exist */}
      {displayTeam.length > 0 && (
        <section className="py-16 md:py-24 px-6 md:px-12 border-b border-[#e5e7eb]">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#95228A] mb-3">The Team</p>
                <h2 className="text-3xl md:text-4xl font-bold text-[#320547] tracking-tight">
                  Leadership
                </h2>
              </div>
              <p className="text-[#6b7280] max-w-md">
                A talented group passionate about creating exceptional digital experiences.
              </p>
            </div>
            
            {/* Team Grid - Larger photos */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {displayTeam.slice(0, 6).map((member, index) => (
                <TeamMember key={member.id} member={member} index={index} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Culture - Dense Bento Grid */}
      <section className="border-b border-[#e5e7eb]">
        <div className="grid lg:grid-cols-4">
          {/* Text Block */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-8 md:p-12 bg-[#320547] text-white flex flex-col justify-center"
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/70 mb-3">Life at Navaris</p>
            <h2 className="text-3xl font-bold tracking-tight mb-4">
              {settings?.culture_title || 'Our Culture'}
            </h2>
            <p className="text-white/80 leading-relaxed">
              {settings?.culture_description || 'Where creativity thrives and every voice matters.'}
            </p>
          </motion.div>
          
          {/* Images */}
          {displayCulture.slice(0, 3).map((image, index) => (
            <motion.div
              key={image.id}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="relative h-64 lg:h-auto overflow-hidden group cursor-pointer"
            >
              <img 
                src={image.image_url} 
                alt={image.title || image.caption || 'Culture'}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              {/* Hover overlay with title and subtext */}
              <div className="absolute inset-0 bg-[#2a2a2a]/70 opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col justify-center items-center text-center p-4">
                <h4 className="text-white text-xl font-bold mb-2 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                  {image.title || image.caption || 'Our Team'}
                </h4>
                <p className="text-white/80 text-sm transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300 delay-75">
                  {image.caption || 'Life at Navaris Digital'}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section - Minimal */}
      <section className="py-16 md:py-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#320547] tracking-tight mb-6">
              Ready to Build Something Amazing?
            </h2>
            <p className="text-[#6b7280] text-lg mb-8">
              Let's discuss how we can bring your digital vision to life.
            </p>
            <motion.a
              href="/contact"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#320547] text-white font-semibold rounded-sm hover:bg-[#95228A] transition-colors"
              data-testid="cta-button"
            >
              Get in Touch
              <ChevronRight size={20} />
            </motion.a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
